import { useTranslation } from "react-i18next";
import { CreateOrUpdateUserAsAdminForm } from "./CreateOrUpdateUserAsAdminForm";
import { CreateOrUpdateUserAsAdminPermissionsList } from "./CreateOrUpdateUserAsAdminPermissionsList";
import { useForm } from "react-hook-form";
import {
  ICreatorDetailsPermission,
  IEnduserDetailsPermission,
  InviteExternalFormData,
} from "src/features/team/state/teamInterfaces";
import { FooterButtons } from "../FooterButtons";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  editCreatorPerms,
  editEnduserPerms,
  getCreateOrEditUserDraft,
  getCreators,
  getEndusers,
  inviteCreatorWithPerms,
  inviteEnduserWithPerms,
  inviteExternalEnduserWithoutEmail,
} from "src/features/team/state/teamSlice";
import { Checkbox } from "../Input";
import {
  getSearchIndex,
  fetchSearchIndex,
} from "src/features/projectGroups/state/projectGroupsSlice";
import { useAppDispatch } from "src/common/state/hooks";
import { COLLABORATORS_TABLE_COMPONENT } from "src/pages/Collaborators/CollaboratorsList";
import { PARTICIPANTS_TABLE_COMPONENT } from "src/pages/Participants/ParticipantsList";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { displayProperAlert } from "src/common/util/displayProperAlert";
import { RootState } from "src/common/state/store";
import {
  fetchSubscription,
  getSubscriptionFeature,
} from "src/features/subscription/state/subscriptionSlice";
import { AvailableFeatureCounter } from "../AvailableCounter/AvailableFeatureCounter";
import { trackEvent } from "src/trackEvent";

const NEW_SOMEONE_COMPONENT_ID = "newSomeoneComponentId";

export type IUserRole = "creator" | "enduser" | "simplifiedEnduser";

interface ICreateOrUpdateUserAsAdminProps {
  mode: "create" | "update";
  userRole: IUserRole;
  userId?: number;
  projectGroupId?: number; // Story 19.3: Optional filter by project-group
}

interface ICreateOrUpdateUserPayload extends InviteExternalFormData {
  itemsToBeGivenAccess: number[];
}

export const CreateOrUpdateUserAsAdmin = ({
  mode,
  userRole,
  projectGroupId,
}: ICreateOrUpdateUserAsAdminProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currentUser = useSelector(getCurrentUser);
  const searchIndex = useSelector(getSearchIndex);

  // Story 19.3: Filter searchIndex if projectGroupId is provided
  const filteredSearchIndex = useMemo(() => {
    if (projectGroupId) {
      return searchIndex.filter(
        (item) => item.type !== "project" || item.projectGroupId === projectGroupId,
      );
    }
    return searchIndex;
  }, [searchIndex, projectGroupId]);

  const userDetails = useSelector(getCreateOrEditUserDraft);
  // When projectGroupId is set, start with empty permissions to avoid flicker
  // (the useEffect below will populate the filtered list on first render)
  const [permissions, setPermissions] = useState<
    (ICreatorDetailsPermission | IEnduserDetailsPermission)[]
  >(
    projectGroupId
      ? []
      : userRole === "creator"
      ? (userDetails.isUserAllowedByProjectOrProjectGroupArray as ICreatorDetailsPermission[])
      : (userDetails.isUserAllowedByProjectOrProjectGroupArray as IEnduserDetailsPermission[]),
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameOfIdKey = userRole === "creator" ? "projectGroupId" : "projectId";

  const collaborators = useSelector(getCreators);
  const collaboratorsFeature = useSelector((state: RootState) =>
    getSubscriptionFeature(state, "number-of-available-creator-accounts"),
  );

  const currentNumberOfAvailableCollaborators = collaboratorsFeature
    ? collaboratorsFeature.QuantityCurrent
    : 0;

  const participants = useSelector(getEndusers);
  const participantsFeature = useSelector((state: RootState) =>
    getSubscriptionFeature(state, "number-of-available-enduser-accounts"),
  );

  const currentNumberOfAvailableParticipants = participantsFeature
    ? participantsFeature.QuantityCurrent
    : 0;

  useEffect(() => {
    dispatch(fetchSubscription({ componentId: NEW_SOMEONE_COMPONENT_ID }));
    dispatch(fetchSearchIndex({}));
  }, [dispatch]);

  const handleSelectAll = () => {
    setPermissions(
      permissions.map((permission: any) => {
        return {
          ...permission,
          allowed: permissions.filter((p: any) => p.allowed).length > 0 ? false : true,
        };
      }),
    );
  };

  const handleClick = (e: any) => {
    const { id } = e.target;
    // State update logic
    if (userRole === "simplifiedEnduser") {
      const thereIsExactlyZeroOrOnePermission =
        permissions.filter((p: any) => p.allowed).length <= 1;

      setPermissions(
        permissions.map((permission: any) => {
          return {
            ...permission,
            allowed:
              Number(permission[nameOfIdKey]) === Number(id) && thereIsExactlyZeroOrOnePermission
                ? !permission.allowed
                : false,
          };
        }),
      );
    } else {
      setPermissions(
        permissions.map((permission: any) => {
          return {
            ...permission,
            allowed:
              Number(permission[nameOfIdKey]) === Number(id)
                ? !permission.allowed
                : permission.allowed,
          };
        }),
      );
    }
  };

  const checkIfSubmitButtonShouldBeDisabled = () => {
    if (mode === "create") {
      switch (userRole) {
        case "simplifiedEnduser":
          return permissions.filter((p: any) => p.allowed).length === 0;
        case "creator":
          return currentNumberOfAvailableCollaborators === 0;
        case "enduser":
          return currentNumberOfAvailableParticipants === 0;
        default:
          return false;
      }
    }
    return false;
  };
  const nextButtonShouldBeDisabled = checkIfSubmitButtonShouldBeDisabled();

  const deduceNextDisabledTooltipText = () => {
    if (nextButtonShouldBeDisabled) {
      if (userRole === "enduser" || userRole === "creator") {
        return t("tooltip.noSlotAvailable");
      } else {
        return t("tooltip.pleaseSelectOneProjectToInviteSimplifiedEnduser");
      }
    }
    return "";
  };

  const rows = permissions.map((permission: any, i: number) => {
    const values =
      userRole === "creator"
        ? {
            id: String(permission["projectGroupId"]),
            allowed: permission.allowed,
            name: permission["projectGroupName"],
            projectGroupInWhichItIs: "",
          }
        : {
            id: String(permission["projectId"]),
            allowed: permission.allowed,
            name: permission["projectName"],
            projectGroupInWhichItIs: permission["projectGroupName"],
          };

    return {
      checkbox: (
        <Checkbox
          checked={permission.allowed}
          key={`${i}-${values.id}`}
          id={values.id}
          value={values.id}
          data-testid={`permission-${values.id}`}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e);
          }}
        />
      ),
      name: values.name,
      projectGroupInWhichItIs: values.projectGroupInWhichItIs,
    };
  });

  const {
    register,
    handleSubmit,
    // watch,
    setValue,
    formState: { errors },
  } = useForm<ICreateOrUpdateUserPayload>();
  let ref: any;
  const clearForm = () => {
    if (ref) {
      ref.reset();
    }
  };

  useEffect(() => {
    if (mode === "update") {
      setValue("newUserFirstName", userDetails.firstname);
      setValue("newUserLastName", userDetails.lastname);
      setValue("newUserEmail", userDetails.email);

      // Fix for WIXAR-4562: Build full permissions list from filteredSearchIndex,
      // then merge user's existing permissions
      if (userRole === "creator") {
        // Creators: Show all ProjectGroups
        const creatorPerms =
          userDetails.isUserAllowedByProjectOrProjectGroupArray as ICreatorDetailsPermission[];
        const userPermissionIds = new Set(
          creatorPerms.filter((p) => p.allowed).map((p) => p.projectGroupId),
        );
        const projectGroupPermissions = filteredSearchIndex
          .filter((item) => item.type === "projectGroup")
          .map((pg) => ({
            allowed: userPermissionIds.has(pg.id),
            projectGroupName: pg.name,
            projectGroupId: pg.id,
          }));
        setPermissions(projectGroupPermissions);
      } else if (userRole === "enduser" || userRole === "simplifiedEnduser") {
        // Build ProjectGroup authType map
        const projectGroupAuthTypes = new Map<number, string>();
        filteredSearchIndex
          .filter((item) => item.type === "projectGroup")
          .forEach((pg) => {
            if (pg.authType) {
              projectGroupAuthTypes.set(pg.id, pg.authType);
            }
          });

        // Get all projects with their parent ProjectGroup names
        const projectGroupNames = new Map<number, string>();
        filteredSearchIndex
          .filter((item) => item.type === "projectGroup")
          .forEach((pg) => {
            projectGroupNames.set(pg.id, pg.name);
          });

        // Get user's existing project permissions (only allowed ones)
        const enduserPerms =
          userDetails.isUserAllowedByProjectOrProjectGroupArray as IEnduserDetailsPermission[];
        const userPermissionIds = new Set(
          enduserPerms.filter((p) => p.allowed).map((p) => p.projectId),
        );

        // Filter projects based on authType
        const projectPermissionsCustomObjects = filteredSearchIndex
          .filter((item) => {
            if (item.type !== "project") return false;

            const authType = projectGroupAuthTypes.get(item.projectGroupId);
            if (!authType || authType === "none") return false;

            if (userRole === "simplifiedEnduser") {
              return authType === "simplified";
            } else {
              // Regular enduser: show standard/classic, NOT simplified or none
              return authType !== "simplified";
            }
          })
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((project) => ({
            projectId: project.id,
            allowed: userPermissionIds.has(project.id),
            projectName: project.name,
            projectGroupName: projectGroupNames.get(project.projectGroupId) || "",
            elementOrder: project.order || 0,
          })) as IEnduserDetailsPermission[];

        setPermissions(projectPermissionsCustomObjects);
      }
    }
    if (mode === "create") {
      // Story 14: Build permissions from filteredSearchIndex
      if (userRole === "creator") {
        // Creators: Show all ProjectGroups
        const projectGroupPermissions = filteredSearchIndex
          .filter((item) => item.type === "projectGroup")
          .map((pg) => ({
            allowed: false,
            projectGroupName: pg.name,
            projectGroupId: pg.id,
          }));
        setPermissions(projectGroupPermissions);
      }
      if (userRole === "enduser" || userRole === "simplifiedEnduser") {
        // Build ProjectGroup authType map
        const projectGroupAuthTypes = new Map<number, string>();
        filteredSearchIndex
          .filter((item) => item.type === "projectGroup")
          .forEach((pg) => {
            if (pg.authType) {
              projectGroupAuthTypes.set(pg.id, pg.authType);
            }
          });

        // Get all projects with their parent ProjectGroup names
        const projectGroupNames = new Map<number, string>();
        filteredSearchIndex
          .filter((item) => item.type === "projectGroup")
          .forEach((pg) => {
            projectGroupNames.set(pg.id, pg.name);
          });

        // Filter projects based on authType
        const projectPermissionsCustomObjects = filteredSearchIndex
          .filter((item) => {
            if (item.type !== "project") return false;

            const authType = projectGroupAuthTypes.get(item.projectGroupId);
            if (!authType || authType === "none") return false;

            if (userRole === "simplifiedEnduser") {
              return authType === "simplified";
            } else {
              // Regular enduser: show standard/classic, NOT simplified or none
              return authType !== "simplified";
            }
          })
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((project) => ({
            projectId: project.id,
            allowed: false,
            projectName: project.name,
            projectGroupName: projectGroupNames.get(project.projectGroupId) || "",
            elementOrder: project.order || 0,
          })) as IEnduserDetailsPermission[];

        setPermissions(projectPermissionsCustomObjects);
      }
    }
  }, [userDetails.isUserAllowedByProjectOrProjectGroupArray, filteredSearchIndex, mode, userRole]);

  const onSubmit = (data: ICreateOrUpdateUserPayload) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    // Story 19.3: Determine navigation path based on context
    const getNavigationPath = () => {
      if (projectGroupId) {
        return `/courses/${projectGroupId}/learners`;
      }
      return userRole === "creator" ? "/team" : "/learners";
    };

    if (userRole === "creator") {
      if (mode === "create") {
        trackEvent("collaborator-invite");
        dispatch(
          inviteCreatorWithPerms({
            firstname: data.newUserFirstName,
            lastname: data.newUserLastName,
            email: data.newUserEmail,
            pgIds: permissions.filter((p) => p.allowed).map((p: any) => Number(p.projectGroupId)),
            componentId: COLLABORATORS_TABLE_COMPONENT,
          }),
        ).then((res: any) => {
          navigate(getNavigationPath());
          displayProperAlert({
            httpStatus: res.payload.status,
            successMsg: "alerts.collaboratorInvitedSuccessfully",
            errorMsg: "alerts.collaboratorInvitedFail",
            dispatch,
            t,
          });
        });
      }
      if (mode === "update") {
        dispatch(
          editCreatorPerms({
            userId: userDetails.id,
            allowedPgIds: permissions
              .filter((p) => p.allowed)
              .map((p: any) => Number(p.projectGroupId)),
            componentId: COLLABORATORS_TABLE_COMPONENT,
          }),
        ).then((res: any) => {
          navigate(getNavigationPath());
          displayProperAlert({
            httpStatus: res.payload.status,
            successMsg: "alerts.collaboratorPermissionsEditedSuccessfully",
            errorMsg: "alerts.collaboratorPermissionsEditedFail",
            dispatch,
            t,
          });
        });
      }
    }

    if (userRole === "enduser" || userRole === "simplifiedEnduser") {
      if (mode === "create" && userRole === "enduser") {
        dispatch(
          inviteEnduserWithPerms({
            firstname: data.newUserFirstName,
            lastname: data.newUserLastName,
            email: data.newUserEmail,
            projectIds: permissions.filter((p) => p.allowed).map((p: any) => Number(p.projectId)),
            componentId: PARTICIPANTS_TABLE_COMPONENT,
          }),
        ).then((res: any) => {
          navigate(getNavigationPath());
          displayProperAlert({
            httpStatus: res.payload.status,
            successMsg: "alerts.participantInvitedSuccessfully",
            errorMsg: "alerts.participantInvitedFail",
            dispatch,
            t,
          });
        });
      }
      if (mode === "update") {
        // Story 19.6: When editing from a project-group context, preserve permissions
        // for projects in OTHER project-groups (only modify the current PG's permissions)
        let allowedProjectIds = permissions
          .filter((p) => p.allowed)
          .map((p: any) => Number(p.projectId));

        if (projectGroupId) {
          const currentPGProjectIds = new Set(
            searchIndex
              .filter((item) => item.type === "project" && item.projectGroupId === projectGroupId)
              .map((item) => item.id),
          );
          const otherPGAllowedIds = (
            userDetails.isUserAllowedByProjectOrProjectGroupArray as IEnduserDetailsPermission[]
          )
            .filter((p) => p.allowed && !currentPGProjectIds.has(p.projectId))
            .map((p) => Number(p.projectId));

          allowedProjectIds = [...allowedProjectIds, ...otherPGAllowedIds];
        }

        dispatch(
          editEnduserPerms({
            userId: userDetails.id,
            allowedProjectIds,
            componentId: PARTICIPANTS_TABLE_COMPONENT,
          }),
        ).then((res: any) => {
          navigate(getNavigationPath());
          displayProperAlert({
            httpStatus: res.payload.status,
            successMsg: "alerts.participantPermissionsEditedSuccessfully",
            errorMsg: "alerts.participantPermissionsEditedFail",
            dispatch,
            t,
          });
        });
      }
    }

    if (userRole === "simplifiedEnduser" && mode === "create") {
      const checkedPerm = permissions.find((p) => p.allowed) as { projectId: number };
      dispatch(
        inviteExternalEnduserWithoutEmail({
          inviterId: currentUser ? currentUser.id : -1,
          projectId: checkedPerm.projectId,
          newUserFirstName: data.newUserFirstName,
          newUserLastName: data.newUserLastName,
          componentId: PARTICIPANTS_TABLE_COMPONENT,
        }),
      ).then((res: any) => {
        navigate(getNavigationPath());
        displayProperAlert({
          httpStatus: res.payload.status,
          successMsg: "alerts.participantInvitedSuccessfully",
          errorMsg: "alerts.participantInvitedFail",
          dispatch,
          t,
        });
      });
    }

    clearForm();
  };

  const Title = () => {
    let string = t(`pages.${userRole === "creator" ? "collaborators" : "participants"}.${mode}`);
    if (userRole === "simplifiedEnduser" && mode === "create") {
      string = t("pages.participants.createSimplified");
    }
    return (
      <div className="w-full flex items-center justify-evenly my-8">
        <div className="w-1/3">
          {userRole === "creator" ? (
            <AvailableFeatureCounter
              className="w-80 h-28"
              currentNumberOfAvailable={currentNumberOfAvailableCollaborators}
              totalNumberOfAvailable={
                collaboratorsFeature
                  ? collaboratorsFeature.QuantityCurrent + collaborators.length
                  : 0
              }
              text={t("pages.collaborators.collaborators")}
            />
          ) : null}
          {userRole === "enduser" || userRole === "simplifiedEnduser" ? (
            <AvailableFeatureCounter
              className="w-80 h-28"
              currentNumberOfAvailable={currentNumberOfAvailableParticipants}
              totalNumberOfAvailable={
                participantsFeature ? participantsFeature.QuantityCurrent + participants.length : 0
              }
              text={t("pages.participants.participants")}
            />
          ) : null}
        </div>
        <div className="flex flex-col items-center w-1/3">
          <h3 className="w-full text-center mt-8 mb-8 text-2xl font-bold">{string}</h3>
        </div>
        <div className="w-1/3"></div>
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col justify-between">
      <div>
        <Title />
        <form className="flex justify-center">
          <div className="h-full w-4/5 flex">
            <div className="flex">
              <CreateOrUpdateUserAsAdminForm
                register={register}
                errors={errors}
                isFormSimplified={userRole === "simplifiedEnduser"}
                readOnly={mode === "update"}
                {...(mode === "update" ? { ...userDetails } : {})}
              />
            </div>
            <div className="flex flex-1">
              <CreateOrUpdateUserAsAdminPermissionsList
                rows={rows}
                userRole={userRole}
                isCheckAllChecked={
                  permissions.length > 0 &&
                  permissions.every((permission: any) => permission.allowed)
                }
                handleSelectAll={handleSelectAll}
                setPermissions={setPermissions}
                permissions={permissions}
              />
            </div>
          </div>
        </form>
      </div>

      <FooterButtons
        cancelText={t("general.cancel")}
        cancelCb={() => {
          // Story 19.3: Use explicit navigation path instead of history-based navigate(-1)
          // to ensure consistent behavior when page is bookmarked or refreshed
          const navigationPath = projectGroupId
            ? `/courses/${projectGroupId}/learners`
            : userRole === "creator"
            ? "/team"
            : "/learners";
          navigate(navigationPath);
        }}
        nextText={mode === "create" ? t("general.create") : t("general.update")}
        nextCb={() => {
          handleSubmit(onSubmit)(); // asciant MVP https://github.com/react-hook-form/react-hook-form/issues/566#issuecomment-612328736
        }}
        nextDisabled={nextButtonShouldBeDisabled || isSubmitting}
        nextDisabledTooltipText={deduceNextDisabledTooltipText()}
      />
    </div>
  );
};
